var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as path from "path-browserify";
import * as Url from "url";
import retry from "./retry";
import * as BluebirdPromise from "bluebird";
const METRIC_FILE_UPLOAD = "BackofficeWebApp/FileUploadMetrics";
const METRIC_FILE_PART_UPLOAD = "BackofficeWebApp/FilePartUploadMetrics";
export var UploadMode;
(function (UploadMode) {
    UploadMode["STANDARD"] = "standard";
    UploadMode["ONBOARDING_WIZARD"] = "onboardingWizard";
    UploadMode["NEW_PROJECT_REQUEST"] = "newProjectRequest";
})(UploadMode || (UploadMode = {}));
export default class FileUploadManager {
    onUploadCompleted(uploadResult) {
        this.uploadIsInProgress = false;
        this.log("Upload completed");
        this.options.onUploadCompleted(Object.assign(Object.assign({}, uploadResult), { file: this.file, filePath: path.join(this.getS3RootPath(), this.getRelativeFilePath(this.file.name)) }));
    }
    onUploadFailed(errorThrown) {
        this.uploadIsInProgress = false;
        this.options.logFunction([
            "Upload failed with server error:",
            errorThrown.message,
            this.file.name,
        ].join(" "));
        this.options.onUploadFailed(errorThrown);
    }
    getRelativeFilePath(fileName) {
        return path.join(this.options.customerId, this.options.siteId, this.options.uploadPath, fileName);
    }
    getS3RootPath() {
        switch (this.options.uploadMode) {
            case UploadMode.STANDARD:
                return "_upload";
            case UploadMode.ONBOARDING_WIZARD:
                return "_upload/_onboarding-wizard-files";
            case UploadMode.NEW_PROJECT_REQUEST:
                return "_upload/_new-project-request-files";
            default:
                return "_upload";
        }
    }
    getEndpointRootPath() {
        switch (this.options.uploadMode) {
            case UploadMode.STANDARD:
                return "upload";
            case UploadMode.ONBOARDING_WIZARD:
                return "onboarding-wizard";
            case UploadMode.NEW_PROJECT_REQUEST:
                return "new-project-request";
            default:
                return "upload";
        }
    }
    fetchJwtToken() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.options.getJwtToken();
        });
    }
    log(msg) {
        this.options.logFunction(`[${this.file.name}] ${msg}`);
    }
    retry(asyncCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            let retriesNumber = 0;
            const result = yield retry(() => {
                retriesNumber++;
                return asyncCallback();
            }, this.options.retryIntervalMs, this.options.maxRetries);
            return { result, retriesNumber };
        });
    }
    constructor(axios, file, options) {
        this.fileUploadMetrics = {};
        this.file = file;
        this.axios = axios;
        this.options = Object.assign({ partSizeBytes: 5 * 1024 * 1024, requestTimeoutMs: 60 * 1000, maxRetries: 5, retryIntervalMs: 2 * 1000, maxParallelRequests: 4, onPartUploaded: () => {
                /* noop */
            }, uploadMode: UploadMode.STANDARD, extraHeaders: {} }, options);
        this.etags = [];
        return this;
    }
    startUpload() {
        if (this.uploadIsInProgress) {
            return;
        }
        (() => __awaiter(this, void 0, void 0, function* () {
            try {
                this.uploadIsInProgress = true;
                const fileUploadStartTime = Date.now();
                let uploadResult;
                if (this.file.size > this.options.partSizeBytes) {
                    uploadResult = yield this.uploadMultipartFile();
                }
                else {
                    uploadResult = yield this.uploadSimpleFile();
                }
                const fileUploadTimeSeconds = (Date.now() - fileUploadStartTime) / 1000;
                this.fileUploadMetrics.uploadTimeSec = fileUploadTimeSeconds;
                this.fileUploadMetrics.sizeMb = this.file.size / 1e6;
                this.fileUploadMetrics.uploadSpeedMBitsPerSec =
                    (this.fileUploadMetrics.sizeMb * 8) / fileUploadTimeSeconds;
                this.options.metricFunction(METRIC_FILE_UPLOAD, this.fileUploadMetrics);
                this.onUploadCompleted(uploadResult);
            }
            catch (err) {
                this.onUploadFailed(err);
            }
        }))();
    }
    uploadMultipartFile() {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = Url.resolve(this.options.bspHost, path.join(this.getEndpointRootPath(), this.getRelativeFilePath(this.file.name)));
            const { result } = yield this.retry(() => __awaiter(this, void 0, void 0, function* () {
                const uploadId = yield this.initiateMultipartUpload(endpoint);
                yield this.uploadParts(endpoint, uploadId);
                const partsAssembleStartTime = Date.now();
                const uploadResult = yield this.completeMultipartUpload(endpoint, uploadId, this.etags);
                this.fileUploadMetrics.assembleSec =
                    (Date.now() - partsAssembleStartTime) / 1000;
                return uploadResult;
            }));
            return result;
        });
    }
    uploadSimpleFile() {
        return __awaiter(this, void 0, void 0, function* () {
            const blob = this.file.slice(0, this.file.size);
            this.fileUploadMetrics.partsCnt = 1;
            const partUploadMetrics = {};
            const endpoint = Url.resolve(this.options.bspHost, path.join(this.getEndpointRootPath(), this.getRelativeFilePath(this.file.name)));
            const partUploadStartTime = Date.now();
            const { retriesNumber, result } = yield this.retry(() => this.uploadWholeFile(endpoint, blob));
            const partUploadTimeSec = (Date.now() - partUploadStartTime) / 1000;
            partUploadMetrics.uploadTimeSec = partUploadTimeSec;
            partUploadMetrics.sizeMb = blob.size / 1e6;
            partUploadMetrics.uploadSpeedMBitsPerSec =
                (partUploadMetrics.sizeMb * 8) / partUploadTimeSec;
            partUploadMetrics.retriesCnt = retriesNumber;
            this.options.metricFunction(METRIC_FILE_PART_UPLOAD, partUploadMetrics);
            return result;
        });
    }
    initiateMultipartUpload(endpoint) {
        return __awaiter(this, void 0, void 0, function* () {
            this.log("Initiating multipart upload");
            const jwtToken = yield this.fetchJwtToken();
            return this.axios
                .request({
                method: "POST",
                url: endpoint + "?uploads",
                headers: {
                    Authorization: `Bearer ${jwtToken}`,
                    "Content-Disposition": "attachment",
                },
                timeout: this.options.requestTimeoutMs,
            })
                .then((res) => {
                // parse response to extract UploadId
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(res.data, "text/xml");
                if (!xmlDoc || !xmlDoc.getElementsByTagName("UploadId").length) {
                    throw new Error("Could not receive UploadId");
                }
                const uploadId = xmlDoc.getElementsByTagName("UploadId")[0].textContent;
                if (!uploadId) {
                    throw new Error("Failed to get uploadId from xml");
                }
                this.log(`Multipart upload initiated, uploadId is ${uploadId}`);
                return uploadId;
            })
                .catch((err) => {
                this.log(`Failed initiating multipart upload: ${err.message}`);
                throw err;
            });
        });
    }
    uploadParts(endpoint, uploadId) {
        let start = 0;
        let end;
        let partNum = 0;
        const blobs = [];
        while (start < this.file.size) {
            end = Math.min(start + this.options.partSizeBytes, this.file.size);
            const filePart = this.file.slice(start, end);
            // this is to prevent push blob with 0Kb
            if (filePart.size > 0) {
                blobs.push(filePart);
            }
            start = this.options.partSizeBytes * ++partNum;
        }
        let partsUploaded = 0;
        const partsTotal = blobs.length;
        this.fileUploadMetrics.partsCnt = partsTotal;
        return BluebirdPromise.map(blobs, (blob, index) => __awaiter(this, void 0, void 0, function* () {
            const partUploadMetrics = {};
            const { retriesNumber } = yield this.retry(() => __awaiter(this, void 0, void 0, function* () {
                const partUploadStartTime = Date.now();
                yield this.uploadFilePart(endpoint, blob, uploadId, index);
                const partUploadTimeSec = (Date.now() - partUploadStartTime) / 1000;
                partUploadMetrics.uploadTimeSec = partUploadTimeSec;
                partUploadMetrics.sizeMb = blob.size / 1e6;
                partUploadMetrics.uploadSpeedMBitsPerSec =
                    (partUploadMetrics.sizeMb * 8) / partUploadTimeSec;
            }));
            partUploadMetrics.retriesCnt = retriesNumber;
            ++partsUploaded;
            this.log(`Parts uploaded: ${partsUploaded}. Parts total ${partsTotal}`);
            this.options.onPartUploaded({
                partsTotal,
                partsUploaded,
                uploadTimeSec: partUploadMetrics.uploadTimeSec,
                sizeMb: partUploadMetrics.sizeMb,
                uploadSpeedMBitsPerSec: partUploadMetrics.uploadSpeedMBitsPerSec,
            });
            this.options.metricFunction(METRIC_FILE_PART_UPLOAD, partUploadMetrics);
        }), {
            concurrency: this.options.maxParallelRequests,
        });
    }
    uploadWholeFile(endpoint, blob) {
        return __awaiter(this, void 0, void 0, function* () {
            this.log(`Sending file as a whole`);
            const jwtToken = yield this.fetchJwtToken();
            const { onPartUploaded } = this.options;
            // Emulate metrics for whole file upload progress event
            let lastSizeMb = 0;
            let lastUploadStartTime = Date.now();
            return this.axios
                .request({
                method: "PUT",
                url: endpoint,
                headers: Object.assign({ Authorization: `Bearer ${jwtToken}`, "Content-Disposition": "attachment" }, this.options.extraHeaders),
                data: blob,
                onUploadProgress(event) {
                    const uploadTimeSec = (Date.now() - lastUploadStartTime) / 1000;
                    const sizeLoadedMb = (event.loaded - lastSizeMb) / 1e6;
                    const uploadSpeedMBitsPerSec = (sizeLoadedMb * 8) / uploadTimeSec;
                    // In case of a single file "partsTotal" is always 1
                    // and "partsUploaded" is between 0 and 1
                    const partsUploaded = event.loaded / event.total;
                    lastUploadStartTime = Date.now();
                    lastSizeMb = sizeLoadedMb;
                    onPartUploaded({
                        uploadTimeSec,
                        uploadSpeedMBitsPerSec,
                        partsTotal: 1,
                        partsUploaded,
                        sizeMb: sizeLoadedMb,
                    });
                },
            })
                .catch((err) => {
                this.log(`File upload failed ${err.message}`);
                throw err;
            })
                .then((res) => {
                if (res && res.status === 200) {
                    const key = res.headers["upload-key"];
                    const location = res.headers["upload-location"];
                    const bucket = res.headers["upload-bucket"];
                    return { key, location, bucket };
                }
                const errorMsg = "uploadWholeFile: Upload returned non 200 status code";
                this.log(errorMsg);
                throw new Error(errorMsg);
            });
        });
    }
    uploadFilePart(endpoint, blob, uploadId, partNum = 0) {
        return __awaiter(this, void 0, void 0, function* () {
            this.log(`Sending part ${partNum}`);
            const jwtToken = yield this.fetchJwtToken();
            return this.axios
                .request({
                method: "PUT",
                url: `${endpoint}?partNumber=${partNum + 1}&uploadId=${uploadId}`,
                timeout: this.options.requestTimeoutMs,
                headers: Object.assign({ Authorization: `Bearer ${jwtToken}` }, this.options.extraHeaders),
                data: blob,
            })
                .catch((err) => {
                this.log(`Part upload failed ${err.message}`);
                throw err;
            })
                .then((res) => {
                if (!res || res.status !== 200) {
                    throw new Error(`File upload response is ${res.status}: ${res.data}`);
                }
                const etag = res.headers.etag;
                if (!etag) {
                    throw new Error(`Failed to get ETag`);
                }
                // TODO: don't use class variable for etags
                this.etags[partNum] = etag;
                return etag;
            });
        });
    }
    completeMultipartUpload(endpoint, uploadId, etags) {
        return __awaiter(this, void 0, void 0, function* () {
            const jwtToken = yield this.fetchJwtToken();
            return this.axios
                .request({
                method: "POST",
                url: `${endpoint}?uploadId=${uploadId}`,
                headers: {
                    Authorization: `Bearer ${jwtToken}`,
                    "Content-Type": "application/octet-stream; charset=UTF-8",
                },
                data: `<CompleteMultipartUpload xmlns="http://s3.amazonaws.com/doc/2006-03-01/">${etags
                    .map((tag, partNum) => `<Part><ETag>${tag}</ETag><PartNumber>${partNum + 1}</PartNumber></Part>`)
                    .join("")}</CompleteMultipartUpload>`,
            })
                .then((res) => {
                const key = this.getXmlProperty(res.data, "Key");
                const location = this.getXmlProperty(res.data, "Location");
                const bucket = this.getXmlProperty(res.data, "Bucket");
                return { key, location, bucket };
            });
        });
    }
    getXmlProperty(xmlString, property) {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, "text/xml");
        if (!xmlDoc || !xmlDoc.getElementsByTagName(property).length) {
            throw new Error(`Invalid or 200 error response, ${property} not found: ${xmlString}`);
        }
        const value = xmlDoc.getElementsByTagName(property)[0].textContent;
        if (!value) {
            throw new Error(`Failed to get ${property} property from xml: ${xmlString}`);
        }
        return value;
    }
}
//# sourceMappingURL=FileUploadManager.js.map